import React, { useEffect, useState } from "react";
import {
  User,
  Loader,
  CheckCircle2,
  ShieldCheck,
  Clock3,
  TrendingUp,
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { FaDownload } from "react-icons/fa";
import Sidebar from "../components/employee/Sidebar";
import TopNavbar from "../components/top-navbar/TopNav";
import axios from "axios";
import "../styles/employee/employee-dashboard.css";
import {
  getRecentNotifications,
  getAverageTat,
  getSuccessRate,
  getEmployeeProfileById,
} from "../services/employee/api";

import {
  getCachedAssignedCandidates,
} from "../services/employee/cache";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import EmployeeNotification from "../components/Notification/EmployeeNotification";
import {
  fetchEmployeeActivityFeed,
  fetchEmployeeNotifications,
} from "../services/employee/notificationapi";

const EmployeeDashboard = () => {
  // Dashboard data state
  const [dashboardData, setDashboardData] = useState({
    totalCandidates: 0,
    workInProgress: 0,
    completed: 0,
    verified: 0,
    averageTat: "0 Days",
    successRate: "0%",
    candidateDTOList: [],
  });

  const username = localStorage.getItem("user_name") || "Employee";
  const token = localStorage.getItem("bgc_token");
  const navigate = useNavigate();

  const PIE_COLORS = ["#eab308", "#10b981", "#0ea5e9"];
  const [messages, setMessages] = useState([]);
  const [activityFeed, setActivityFeed] = useState([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const res = await getCachedAssignedCandidates();

        setDashboardData((prev) => ({
          ...prev,
          ...res.data.data,
        }));
      } catch (err) {
        console.error("Error fetching assigned candidates:", err);
      }
    };

    const loadActivityFeed = async () => {
      try {
        const res = await fetchEmployeeActivityFeed();
        setActivityFeed(res?.data || []);
      } catch (err) { }
    };

    const loadNotifications = async () => {
      try {
        const res = await fetchEmployeeNotifications();

        if (res?.data) {
          setMessages(res.data);
        }
      } catch (err) {
        console.error("Error fetching notifications", err);
      }
    };

    const fetchEmployeeMetrics = async () => {
      try {
        const profileRes = await getEmployeeProfileById();
        const employeeId = profileRes.data.data.userid;

        const [tatRes, successRes] = await Promise.all([
          getAverageTat(employeeId),
          getSuccessRate(employeeId),
        ]);

        setDashboardData((prev) => ({
          ...prev,
          averageTat: tatRes?.data?.data?.averageTat || "0 Days",
          successRate: successRes?.data?.data?.successRate || "0.00%",
        }));
      } catch (error) {
        console.error("Error fetching employee metrics", error);
      }
    };

    fetchDashboardData();
    loadActivityFeed();
    loadNotifications();
    // fetchRecentNotifications();
    fetchEmployeeMetrics();
  }, [token]);

  const stats = [
    {
      title: "Total Candidates",
      count: dashboardData.totalCandidates,
      icon: <User size={32} color="#4f46e5" />,
      status: "",
    },
    {
      title: "Work In Progress",
      count: dashboardData.workInProgress,
      icon: <Loader size={32} color="#eab308" />,
      status: "WORK_IN_PROGRESS",
    },
    {
      title: "Completed",
      count: dashboardData.completed,
      icon: <CheckCircle2 size={32} color="#10b981" />,
      status: "COMPLETED",
    },
    {
      title: "Verified",
      count: dashboardData.verified,
      icon: <ShieldCheck size={32} color="#0ea5e9" />,
      status: "VERIFIED",
    },
  ];

  const pieChartData = [
    { name: "WIP", value: dashboardData.workInProgress },
    { name: "Completed", value: dashboardData.completed },
    { name: "Verified", value: dashboardData.verified },
  ];

  const handleStatClick = async (status) => {
    if (typeof status === "string") {
      navigate(`/employee/assignedCandidates/${status}`);
    } else {
      toast.error("Invalid status passed:", status);
    }
  };

  const allCandidates = dashboardData.candidateDTOList;
  const verifiedCompletedCandidates = dashboardData.candidateDTOList.filter(
    (c) => c.bgcStatus === "VERIFIED" || c.bgcStatus === "COMPLETED",
  );

  const handleIncomingEmployeeNotification = (notification) => {
    setMessages((prev) => [
      {
        message: notification.message,
        notificationTime:
          notification.notificationTime || notification.createdAt,
        ...notification,
      },
      ...prev,
    ]);

    setActivityFeed((prev) => [
      {
        description: notification.message,
        createdAt: notification.notificationTime || notification.createdAt,
      },
      ...prev,
    ]);
  };

  return (
    <div className="emp-dashboard-app-container">
      <EmployeeNotification
        onNotification={handleIncomingEmployeeNotification}
      />
      <Sidebar />
      <div className="emp-dashboard-right-panel">
        <TopNavbar />
        <div className="emp-dashboard-container">
          <div className="emp-dashboard-welcome-section">
            <h1 className="emp-dashboard-welcome-title">
              Welcome back, {username}
            </h1>
          </div>

          <div className="emp-dashboard-stats-grid">
            {stats.map((stat, index) => (
              <div
                className="emp-dashboard-stat-card clickable"
                key={index}
                onClick={() => handleStatClick(stat.status)}
              >
                <div className="emp-dashboard-stat-info">
                  <h3 className="emp-dashboard-card-title">{stat.title}</h3>
                  <div className="emp-dashboard-card-count">{stat.count}</div>
                </div>
                <div className="emp-dashboard-stat-icon-container">
                  <div className="emp-dashboard-stat-icon">{stat.icon}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="emp-dashboard-stats-grid">
            <div className="emp-dashboard-stat-card">
              <div className="emp-dashboard-stat-info">
                <h3 className="emp-dashboard-card-title">Average TAT</h3>

                <div className="emp-dashboard-card-count">
                  {dashboardData.averageTat}
                </div>
              </div>

              <div className="emp-dashboard-stat-icon-container">
                <div className="emp-dashboard-stat-icon">
                  <Clock3 size={32} color="#f59e0b" />
                </div>
              </div>
            </div>

            <div className="emp-dashboard-stat-card">
              <div className="emp-dashboard-stat-info">
                <h3 className="emp-dashboard-card-title">Success Rate</h3>

                <div className="emp-dashboard-card-count">
                  {dashboardData.successRate}
                </div>
              </div>

              <div className="emp-dashboard-stat-icon-container">
                <div className="emp-dashboard-stat-icon">
                  <TrendingUp size={32} color="#22c55e" />
                </div>
              </div>
            </div>
          </div>

          <div className="emp-dashboard-main-content-grid">
            <div className="emp-dashboard-content-card emp-dashboard-overall-status-card">
              <h2 className="emp-dashboard-card-header">Overall Status</h2>
              <div className="emp-dashboard-overall-status-content">
                <div className="emp-dashboard-pie-chart-wrapper">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieChartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={55}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                        labelLine={false}
                      >
                        {pieChartData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={PIE_COLORS[index % PIE_COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value, name, props) => [
                          `${value} Candidates`,
                          props.payload.name,
                        ]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="emp-dashboard-pie-chart-legend">
                  {pieChartData.map((entry, index) => (
                    <div
                      key={`legend-${index}`}
                      className="emp-dashboard-legend-item"
                    >
                      <span
                        className="emp-dashboard-legend-color"
                        style={{
                          backgroundColor:
                            PIE_COLORS[index % PIE_COLORS.length],
                        }}
                      ></span>
                      {entry.name}: <strong>{entry.value}</strong>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Activity Feed Section */}
            <div className="emp-dashboard-content-card emp-dashboard-activity-feed-card">
              <h2 className="emp-dashboard-card-header">Activity Feed</h2>
              <div className="emp-dashboard-scrollable-list">
                {activityFeed.length === 0 ? (
                  <div className="emp-dashboard-empty-state">
                    No recent activity.
                  </div>
                ) : (
                  activityFeed.map((activity, index) => (
                    <div
                      key={`activity-${index}`}
                      className="emp-dashboard-activity-item"
                    >
                      <div className="emp-dashboard-activity-text">
                        {activity.description}
                      </div>
                      <div className="emp-dashboard-activity-time">
                        <small>
                          {activity.createdAt
                            ? new Date(activity.createdAt).toLocaleString(
                              "en-IN",
                              {
                                timeZone: "Asia/Kolkata",
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                              },
                            )
                            : ""}
                        </small>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Candidate Lists */}
          <div className="emp-dashboard-lists-container">
            <div className="emp-dashboard-content-card">
              <h2 className="emp-dashboard-card-header">
                Recently Added Candidates
              </h2>
              <div className="emp-dashboard-scrollable-list">
                {allCandidates.length === 0 ? (
                  <div className="emp-dashboard-empty-state">
                    No candidates assigned.
                  </div>
                ) : (
                  allCandidates.map((item, index) => (
                    <div
                      className="emp-dashboard-list-item"
                      key={`rc-${index}`}
                    >
                      <div className="emp-dashboard-list-item-left">
                        <div>
                          <div className="emp-dashboard-item-name">
                            {item.name}
                          </div>
                          <div className="emp-dashboard-item-subtext">
                            {item.date}
                          </div>
                        </div>
                      </div>
                      <button
                        className={`emp-dashboard-status-button ${item.bgcStatus.toLowerCase().replace("_", "-")}`}
                        disabled={item.bgcStatus !== "NOT_STARTED"}
                      >
                        Start_BGC
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="emp-dashboard-content-card">
              <h2 className="emp-dashboard-card-header">All Candidates</h2>
              <div className="emp-dashboard-scrollable-list">
                {allCandidates.length === 0 ? (
                  <div className="emp-dashboard-empty-state">
                    No candidates assigned.
                  </div>
                ) : (
                  allCandidates.map((item, index) => (
                    <div
                      className="emp-dashboard-list-item"
                      key={`rc-${index}`}
                    >
                      <div className="emp-dashboard-list-item-left">
                        <div>
                          <div className="emp-dashboard-item-name">
                            {item.name}
                          </div>
                          <div className="emp-dashboard-item-subtext">
                            {item.email}
                          </div>
                        </div>
                      </div>
                      <span
                        className={`emp-dashboard-status-badge ${item.bgcStatus.toLowerCase().replace("_", "-")}`}
                      >
                        {item.bgcStatus.replace("_", " ")}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="emp-dashboard-content-card">
              <h2 className="emp-dashboard-card-header">
                Verified & Completed
              </h2>
              <div className="emp-dashboard-scrollable-list">
                {verifiedCompletedCandidates.length === 0 ? (
                  <div className="emp-dashboard-empty-state">
                    No verified or completed candidates.
                  </div>
                ) : (
                  verifiedCompletedCandidates.map((item, index) => (
                    <div
                      className="emp-dashboard-list-item"
                      key={`rprt-${index}`}
                    >
                      <div className="emp-dashboard-list-item-left">
                        {" "}
                        <div>
                          <div className="emp-dashboard-item-name">
                            {item.name}
                          </div>
                          <div className="emp-dashboard-item-subtext">
                            {item.email}
                          </div>
                        </div>
                      </div>
                      <FaDownload
                        className="emp-dashboard-download-icon"
                        title="Download Report"
                      />
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeDashboard;
